import moment from "moment";
import { Frequency, RRuleSet } from "rrule";

/** Schedule action. */
export enum ScheduleAction {
    Impulse = "Impulse",
    SetParameter = "SetParameter",
}

/** Schedule item. */
export type ScheduleItem = Readonly<{

    /** ID. */
    id: number;

    /** Parent ID. */
    parentId: number | null;

    /** Name. */
    name: string;

    /** Priority. */
    priority: number;

    /** RRule. */
    rrule: RRuleSet;

    /** User settings. */
    userSettings: {

        /** On which days execute. */
        execute: "weekdays" | "weekends" | null;
    };

    /** Whether is enabled. */
    enabled: boolean;

    /** Start date. */
    startDate: Date;

    /** End date. */
    endDate: Date | null;

    /** Action. */
    action: {

        /** Type. */
        type: ScheduleAction;

        /** Parameter. */
        parameter: string;

        /** Parameter ID. */
        parameterId: number;

        /** Pulse duration. */
        pulseDuration: number;

        /** Value. */
        value: string;
    };

    /** Comment. */
    comment: string;

    /** Color. */
    color: string;

    /** Whether need to write to archive. */
    writeToArchive: boolean;

    /** Archive message. */
    archiveMessage: string;
}>;

/**
 * Whether schedule item is non repeatable.
 * @param item Schedule item.
 */
export function isNonRepeatableItem(item: ScheduleItem): boolean {
    const rrule = item.rrule.rrules()[0];
    return rrule.options.freq === Frequency.YEARLY && rrule.options.count === 1;
}

/**
 * Comparator function for two schedule items.
 * @param item1 Schedule item one.
 * @param item2 Schedule item two.
 * @returns True if items are same, false otherwise.
 */
export function compareScheduleItems(item1: ScheduleItem, item2: ScheduleItem): boolean {
    return item1.id === item2.id &&
        item1.parentId === item2.parentId &&
        item1.name === item2.name &&
        item1.priority === item2.priority &&
        item1.rrule.toString() === item2.rrule.toString() &&
        item1.userSettings.execute === item2.userSettings.execute &&
        item1.enabled === item2.enabled &&
        moment(item1.startDate).isSame(moment(item2.startDate), "s") &&
        (
            item1.endDate instanceof Date && item2.endDate instanceof Date ?
                moment(item1.endDate).isSame(moment(item2.endDate), "s") :
                item1.endDate === item2.endDate
        ) &&
        item1.action.type === item2.action.type &&
        item1.action.parameter === item2.action.parameter &&
        item1.action.pulseDuration === item2.action.pulseDuration &&
        item1.action.value === item2.action.value &&
        item1.action.parameterId === item2.action.parameterId &&
        item1.comment === item2.comment &&
        item1.color === item2.color &&
        item1.writeToArchive === item2.writeToArchive &&
        item1.archiveMessage === item2.archiveMessage;
}
